import React, { useCallback, useEffect, useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Layers, MessageSquare, FileEdit, LogOut, Menu, X, CalendarClock, Settings as SettingsIcon, Calendar as CalendarIcon, UserCircle2, Receipt, Activity } from 'lucide-react';
import { logout, currentAdminEmail } from '@/lib/auth';
import { api } from '@/lib/api';
import { ADMIN } from '@/constants/testIds';
import Logo from '@/components/public/Logo';
import { AdminThemeProvider, useAdminTheme } from '@/lib/theme';
import { logger } from '@/lib/logger';

function AdminShell() {
  const navigate = useNavigate();
  const { theme } = useAdminTheme();
  const [unread, setUnread] = useState(0);
  const [pending, setPending] = useState(0);
  const [todaySessions, setTodaySessions] = useState(0);
  const [unpaidInvoices, setUnpaidInvoices] = useState(0);
  const [brand, setBrand] = useState(null);
  const [open, setOpen] = useState(false);

  // Apply dark utility class when in dark mode so shadcn dark variants work.
  useEffect(() => {
    const el = document.documentElement;
    if (theme.mode === 'dark') el.classList.add('dark'); else el.classList.remove('dark');
    return () => { el.classList.remove('dark'); };
  }, [theme.mode]);

  const fetchOverview = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/overview');
      setUnread(data.unread_messages || 0);
      setPending(data.pending_bookings || 0);
      setTodaySessions(data.today_sessions || 0);
      setUnpaidInvoices(data.unpaid_invoices || 0);
    } catch (err) {
      logger.warn('Failed to fetch admin overview stats', err);
    }
  }, []);

  const fetchBrand = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/content');
      setBrand(data?.brand?.extra || null);
    } catch (err) {
      logger.warn('Failed to fetch brand settings', err);
    }
  }, []);

  useEffect(() => {
    fetchOverview();
    fetchBrand();
    const t = setInterval(fetchOverview, 30000);
    const handler = () => { fetchOverview(); };
    const brandHandler = () => fetchBrand();
    window.addEventListener('pg-refresh-unread', handler);
    window.addEventListener('pg-refresh-brand', brandHandler);
    return () => {
      clearInterval(t);
      window.removeEventListener('pg-refresh-unread', handler);
      window.removeEventListener('pg-refresh-brand', brandHandler);
    };
  }, [fetchOverview, fetchBrand]);

  const doLogout = () => { logout(); navigate('/admin/login', { replace: true }); };

  const items = [
    { to: '/admin/overview', label: 'Overview', icon: LayoutDashboard, testid: ADMIN.navOverview },
    { to: '/admin/analytics', label: 'Analytics', icon: Activity, testid: ADMIN.navAnalytics },
    { to: '/admin/calendar', label: 'Calendar', icon: CalendarIcon, testid: ADMIN.navCalendar, badge: todaySessions, badgeTestId: ADMIN.todayBadge },
    { to: '/admin/clients', label: 'Clients', icon: UserCircle2, testid: ADMIN.navClients },
    { to: '/admin/invoices', label: 'Invoices', icon: Receipt, testid: ADMIN.navInvoices, badge: unpaidInvoices, badgeTestId: ADMIN.invoicesBadge },
    { to: '/admin/bookings', label: 'Bookings', icon: CalendarClock, testid: ADMIN.navBookings, badge: pending, badgeTestId: ADMIN.bookingsBadge },
    { to: '/admin/messages', label: 'Messages', icon: MessageSquare, testid: ADMIN.navMessages, badge: unread, badgeTestId: ADMIN.unreadBadge },
    { to: '/admin/services', label: 'Services & Sessions', icon: Layers, testid: ADMIN.navServices },
    { to: '/admin/content', label: 'Content', icon: FileEdit, testid: ADMIN.navContent },
    { to: '/admin/settings', label: 'Settings', icon: SettingsIcon, testid: ADMIN.navSettings },
  ];

  const Sidebar = (
    <aside data-testid={ADMIN.sidebar} className="admin-sidebar w-72 shrink-0 bg-[#0B0C0F] border-r border-white/5 h-full flex flex-col">
      <div className="px-5 py-6 border-b border-white/5">
        <Logo brand={brand} invert showWordmark />
        <div className="mt-2 text-[11px] uppercase tracking-widest text-white/40">Admin Console</div>
      </div>
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {items.map((it) => (
          <NavLink
            key={it.to}
            to={it.to}
            data-testid={it.testid}
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              `group flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors ${
                isActive ? 'bg-[var(--pg-admin-accent)] text-[var(--pg-admin-accent-fg)] font-semibold' : 'text-white/75 hover:bg-white/5 hover:text-white'
              }`
            }
          >
            {({ isActive }) => (
              <>
                <it.icon className={`w-4 h-4 ${isActive ? '' : 'text-white/60 group-hover:text-white'}`} style={isActive ? { color: 'var(--pg-admin-accent-fg)' } : undefined} />
                <span className="grow">{it.label}</span>
                {typeof it.badge === 'number' && it.badge > 0 && (
                  <span
                    data-testid={it.badgeTestId}
                    className={`inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full text-[11px] font-semibold ${
                      isActive ? 'bg-[#141414] text-[var(--pg-admin-accent)]' : 'bg-[var(--pg-admin-accent)] text-[var(--pg-admin-accent-fg)]'
                    }`}
                  >
                    {it.badge}
                  </span>
                )}
              </>
            )}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-white/5">
        <div className="px-3 pb-3">
          <div className="text-[11px] uppercase tracking-widest text-white/40">Signed in as</div>
          <div className="text-sm text-white/85 truncate">{currentAdminEmail()}</div>
        </div>
        <button data-testid={ADMIN.logoutBtn} onClick={doLogout} className="w-full inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm text-white/85 hover:bg-white/5 transition-colors">
          <LogOut className="w-4 h-4" /> Sign out
        </button>
      </div>
    </aside>
  );

  return (
    <div
      className={`admin-root ${theme.mode === 'dark' ? 'dark' : ''} min-h-screen bg-[#0B0C0F] text-white flex`}
      data-admin-mode={theme.mode}
      data-admin-palette={theme.palette}
    >
      <div className="hidden lg:flex sticky top-0 h-screen">{Sidebar}</div>

      {open && (
        <div className="lg:hidden fixed inset-0 z-40 flex">
          <div className="absolute inset-0 bg-black/60" onClick={() => setOpen(false)} />
          <div className="relative h-full">{Sidebar}</div>
        </div>
      )}

      <div className="flex-1 flex flex-col min-w-0">
        <header className="admin-topbar lg:hidden sticky top-0 z-30 flex items-center justify-between px-4 py-3 border-b border-white/5 bg-[#0B0C0F]/95 backdrop-blur">
          <button aria-label="Menu" onClick={() => setOpen(!open)} className="p-2 rounded-lg hover:bg-white/5">
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
          <Logo brand={brand} invert />
          <button onClick={doLogout} aria-label="Sign out" className="p-2 rounded-lg hover:bg-white/5"><LogOut className="w-5 h-5" /></button>
        </header>
        <main className="p-4 sm:p-6 lg:p-8 max-w-[1400px] w-full mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

export default function AdminLayout() {
  return (
    <AdminThemeProvider>
      <AdminShell />
    </AdminThemeProvider>
  );
}
